import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import _ from "lodash";

import {
  _getAppliedCandidates,
  _getAppliedShifts,
} from "@/services/clientcandidates";
import { handleAPICatch } from "@/utilities/utils";

import { formatAppliedCandidates } from "../../utilities/dataFormatter";
const initialState = {
  isLoading: false,
  isPaginationLoading: false,
  appliedCandidates: [],
  selectedCandidate: [], // list of candidate id
  selectedApplication: [], // list of application id
  selectContractId: [], // list of contract id
  filters: {
    search: "",
    sessionType: [],
    candidateReferenceNo: "",
    contractReferenceNo: "",
    candidateId: [],
    complianceStatus: null,
    miles: null,
    candidateRole: null,
    candidateLicenseNo: "",
    candidatePostcode: "",
    appliedDateTo: "",
    appliedDateFrom: "",
    contractDateFrom: "",
    contractDateTo: "",
  },
  sorting: {
    sortByColumn: "",
    sortByOrder: "",
  },
  totalCount: 0,
  isSelectedAll: false,
  oldFilters: {},
  toggleSelected: false,
  pagination: {
    currentPage: 1,
    totalPage: 1,
    isLastPage: false,
  },
  showFilters: false,
  appliedShiftsLoading: false,
  showAppliedDatesModal: false,
  applicantIdForModal: null,
  appliedContractRefNo: null,
  appliedCandidateName: null,
  fixedDatesForModal: [],
  appliedDatesForModal: [],
};

export const fetchAppliedShifts = createAsyncThunk(
  "candidatesApplied/fetchAppliedShifts",
  async (data, thunkAPI) => {
    const {
      applicant_id = null,
      contract_id = null,
      candidate_id,
      forCalendar = false,
      loading,
      setLoading,
    } = data;

    if (forCalendar) {
      if (loading) return;
      setLoading(true);
    }
    const response = _getAppliedShifts(data);
    response
      .then((res) => {
        const {
          data: { data, contract_ref_no, candidate_name },
        } = res;
        if (forCalendar) {
          let newData = data.map((shift) => {
            return {
              shift_id: shift.shift_id,
              contract_date: shift.contract_date,
            };
          });
          thunkAPI.dispatch(toggleAppliedDatesModal(true));
          thunkAPI.dispatch(updateApplicantIdForModal(applicant_id));
          thunkAPI.dispatch(updateContractRefNo(contract_ref_no));
          thunkAPI.dispatch(updateCandiateName(candidate_name));
          thunkAPI.dispatch(updateAppliedDatesForModal(newData));
          thunkAPI.dispatch(updateFixedDatesForModal(newData));
        } else {
          thunkAPI.dispatch(
            updateCandidateShifts({
              candidate_id: candidate_id,
              shifts: data,
            }),
          );
        }
      })
      .catch((error) => {
        // console.log(error);
        handleAPICatch(error);
      })
      .finally(() => {
        if (forCalendar) {
          setLoading(false);
        }
      });
  },
);

export const fetchAppliedCandidates = createAsyncThunk(
  "candidatesApplied/fetchAppliedCandidates",
  async (data, { dispatch, getState }) => {
    const {
      clientCandidatesApplied: {
        pagination,
        sorting,
        filters,
        appliedCandidates,
      },
    } = getState();

    if (_.isEmpty(data)) {
      data = {
        ...filters,
      };
    }

    if (pagination.isLastPage) {
      return dispatch(setAppliedCandidatesLoading(false));
    }

    dispatch(setPaginationLoading(pagination.currentPage !== 1));
    dispatch(toggleSelectedAll(false));
    const _data = {
      ...data,
      currentPage: pagination.currentPage,
      ...sorting,
    };
    const response = _getAppliedCandidates(_data);
    response
      .then((res) => {
        const {
          data: { candidate_application_list, meta },
        } = res;
        const { currentPage, lastPage, isLastPage, total: totalCount } = meta;
        let data = formatAppliedCandidates(candidate_application_list);

        dispatch(
          updatePagination({
            currentPage: currentPage + 1,
            totalPage: lastPage,
            isLastPage: isLastPage,
          }),
        );

        dispatch(updateTotalCount(totalCount));

        let prevList = currentPage === 1 ? [] : _.uniq(appliedCandidates);

        dispatch(updateAppliedCandidatesList([...prevList, ...data]));
      })
      .catch((error) => {
        handleAPICatch(error);
        // return rejectWithValue(error);
      })
      .finally(() => {
        setTimeout(() => {
          dispatch(setAppliedCandidatesLoading(false));
          dispatch(setPaginationLoading(false));
        }, 10);
      });
  },
);

const candidatesAppliedSlice = createSlice({
  name: "candidatesApplied",
  initialState,
  reducers: {
    /**
     * Updates the applied candidates list in the state with the provided payload.
     *
     * @param {type} state - the current state
     * @param {type} action - the action containing the payload
     * @return {type}
     */
    updateAppliedCandidatesList: (state, action) => {
      state.appliedCandidates = action.payload;
    },
    /**
     * Set the loading state for applied candidates.
     *
     * @param {type} state - the current state
     * @param {type} action - the action payload
     * @return {type} the updated state with isLoading set to the action payload
     */
    setAppliedCandidatesLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setPaginationLoading(state, action) {
      state.isPaginationLoading = action.payload; // New reducer for pagination loading
    },
    /**
     * Update the selected candidate in the state.
     *
     * @param {type} state - the current state
     * @param {type} action - the action containing the payload
     * @return {type} undefined
     */
    updateSelectedCandidate: (state, action) => {
      state.selectedCandidate = action.payload;
      if (action.payload.length === 0) {
        state.isSelectedAll = false;
      }
    },
    /**
     * Updates the selected application in the state.
     *
     * @param {type} state - the current state
     * @param {type} action - the action containing the payload
     * @return {type} undefined
     */
    updateSelectedApplication: (state, action) => {
      state.selectedApplication = action.payload;
    },
    updateSelectedContractId: (state, action) => {
      state.selectContractId = action.payload;
    },
    /**
     * deSelectAllCandidate - Deselects all candidates and applications.
     *
     * @param {Object} state - the state object
     * @param {Object} action - the action object
     * @return {void}
     */
    deSelectAllCandidate: (state, action) => {
      state.selectedCandidate = [];
      state.selectedApplication = [];
      state.selectContractId = [];
      state.isSelectedAll = false;
      state.toggleSelected = !state.toggleSelected;
    },
    /**
     * Updates the filters based on the given state and action.
     *
     * @param {object} state - the current state
     * @param {object} action - the action to be performed
     * @return {void}
     */
    updateFilters: (state, action) => {
      if (_.isEmpty(action.payload)) {
        state.filters = {
          search: state.filters.search,
          ...initialState.filters,
        };
        return;
      } else {
        if (action.payload.reset) {
          state.filters = initialState.filters;
        } else {
          state.filters = { ...state.filters, ...action.payload };
        }
      }
    },
    updateSorting: (state, action) => {
      state.sorting = action.payload;
    },
    /**
     * Updates the shifts of a candidate in the state's appliedCandidates array.
     *
     * @param {Object} state - The current state object
     * @param {Object} action - The action object containing candidate_id and shifts
     * @return {void}
     */
    updateCandidateShifts: (state, action) => {
      const { candidate_id, shifts } = action.payload;
      let applieClone = [...state.appliedCandidates];
      state.appliedCandidates = applieClone.map((candidate) => {
        if (candidate.candidate_id == candidate_id) {
          candidate.shifts = shifts;
        }
        return candidate;
      });
    },
    /**
     * Updates state to show or hide the filter modal.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object containing payload
     * @return {void}
     */
    showFilterModal: (state, action) => {
      if (action.payload) {
        state.oldFilters = state.filters;
      }
      state.showFilters = action.payload;
    },
    /**
     * Updates the pagination state with the provided payload.
     *
     * @param {object} state - The current state object
     * @param {object} action - The action object containing the payload
     * @return {void}
     */
    updatePagination: (state, action) => {
      state.pagination = {
        ...state.pagination,
        ...action.payload,
      };
    },
    /**
     * Update the toggleSelected state based on the action.
     *
     * @param {object} state - the current state object
     * @param {object} action - the action object
     * @return {void}
     */
    updateToggleSelected: (state, action) => {
      state.toggleSelected = !state.toggleSelected;
    },
    /**
     * A function to reset the state filters to their old values.
     *
     * @param {type} state - the current state object
     * @param {type} action - the action being performed
     * @return {type} undefined
     */
    reStateFilters: (state, action) => {
      state.filters = state.oldFilters;
    },
    updateTotalCount: (state, action) => {
      state.totalCount = action.payload;
    },
    toggleSelectedAll: (state, action) => {
      state.isSelectedAll = action.payload;
    },
    updateApplicantIdForModal: (state, action) => {
      state.applicantIdForModal = action.payload;
    },
    updateContractRefNo: (state, action) => {
      state.appliedContractRefNo = action.payload;
    },
    updateCandiateName: (state, action) => {
      state.appliedCandidateName = action.payload;
    },
    updateFixedDatesForModal: (state, action) => {
      state.fixedDatesForModal = action.payload;
    },
    updateAppliedDatesForModal: (state, action) => {
      state.appliedDatesForModal = action.payload;
    },
    toggleAppliedDatesModal: (state, action) => {
      state.showAppliedDatesModal = action.payload;
    },
    updateCandidateStatus: (state, action) => {
      let { unsuitable, sortlisted, isSelectedAll, ignoreIds, ids } =
        action.payload;
      let _ids = ids.map((id) => id.toString());
      let _ignoreIds = ignoreIds.map((id) => id.toString());
      state.appliedCandidates = state.appliedCandidates.map((candidate) => {
        if (_ignoreIds.includes(candidate?.candidate_id?.toString())) {
          return candidate;
        }
        if (
          _ids.includes(candidate?.candidate_id?.toString()) ||
          isSelectedAll
        ) {
          candidate.candidate_unsuitable = unsuitable;
          candidate.candidate_sortlisted = sortlisted;
        }
        return candidate;
      });
    },
  },
});

export const {
  updateAppliedCandidatesList,
  setAppliedCandidatesLoading,
  setPaginationLoading,
  updateSelectedCandidate,
  deSelectAllCandidate,
  updateFilters,
  updateSorting,
  updateCandidateShifts,
  updateSelectedApplication,
  updateSelectedContractId,
  showFilterModal,
  updateContractRefNo,
  updateCandiateName,
  updatePagination,
  updateToggleSelected,
  reStateFilters,
  updateTotalCount,
  toggleSelectedAll,
  updateApplicantIdForModal,
  updateFixedDatesForModal,
  updateAppliedDatesForModal,
  toggleAppliedDatesModal,
  updateCandidateStatus,
} = candidatesAppliedSlice.actions;

export default candidatesAppliedSlice.reducer;
